import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { ModelInfo } from '@/services/types';
import assetService from '@/services/amai/asset.service';

// 小工具 payload 类型定义
export interface FeedToolsPayload {
  model_id_list: string[];
  number: number;
  arguments: Array<{ field_name: string; field_value: string | number | boolean }>;
  // 小工具特有的字段
  tool_type?: 'face_swap' | 'clothing_swap'; // 工具类型：换脸或换装
  source_asset_id?: string; // 源文件资产ID
  target_asset_id?: string; // 目标文件资产ID
  mask_asset_id?: string; // 蒙版文件资产ID（换装时使用）
}

interface ToolsGenerationState {
  // 基础设置
  selectedModels: string[];
  generationCount: number;

  // 工具类型
  activeToolTab: '换脸' | '换装';

  // 文件上传相关
  uploadedFileType: 'image' | 'video' | null;
  sourceAsset: { id: string; url: string } | null; // 源文件
  targetAsset: { id: string; url: string } | null; // 目标文件（换装时使用）
  maskAsset: { id: string; url: string } | null; // 蒙版文件（换装时使用）

  // 模型数据
  models: ModelInfo[];

  // 加载状态
  isLoading: boolean;
  error: string | null;
}

const initialState: ToolsGenerationState = {
  selectedModels: [],
  generationCount: 1,
  activeToolTab: '换脸',
  uploadedFileType: null,
  sourceAsset: null,
  targetAsset: null,
  maskAsset: null,
  models: [],
  isLoading: false,
  error: null,
};

// 异步thunk：应用小工具参数并获取资产信息
export const applyToolsPayloadWithAssets = createAsyncThunk(
  'toolsGeneration/applyToolsPayloadWithAssets',
  async (payload: FeedToolsPayload, { rejectWithValue }) => {
    try {
      // 防御性检查：确保payload存在
      if (!payload) {
        return rejectWithValue('小工具payload为空');
      }

      const result = {
        payload,
        sourceAsset: null as { id: string; url: string } | null,
        targetAsset: null as { id: string; url: string } | null,
        maskAsset: null as { id: string; url: string } | null,
      };

      // 收集需要获取的资产ID
      const assetIds: string[] = [];

      // 从 arguments 数组中提取资产ID
      if (payload.arguments && Array.isArray(payload.arguments)) {
        payload.arguments.forEach(arg => {
          if (typeof arg.field_value === 'string' && arg.field_value.length > 0) {
            // 检查是否是资产ID格式（通常以特定前缀开头）
            if (
              arg.field_name === 'dest_video' ||
              arg.field_name === 'dest_image' ||
              arg.field_name === 'face_image' ||
              arg.field_name === 'source_image' ||
              arg.field_name === 'target_image' ||
              arg.field_name === 'mask_image'
            ) {
              assetIds.push(arg.field_value);
            }
          }
        });
      }

      // 兼容旧的直接字段方式
      if (payload.source_asset_id) assetIds.push(payload.source_asset_id);
      if (payload.target_asset_id) assetIds.push(payload.target_asset_id);
      if (payload.mask_asset_id) assetIds.push(payload.mask_asset_id);

      // 去重
      const uniqueAssetIds = [...new Set(assetIds)];

      // 批量获取资产信息
      if (uniqueAssetIds.length > 0) {
        const res = await assetService.getAssetList({ asset_id_list: uniqueAssetIds });
        if (res.data?.list) {
          const assetMap = new Map(res.data.list.map(asset => [asset.asset_id, asset.url]));

          // 根据 arguments 中的字段名分配资产
          if (payload.arguments && Array.isArray(payload.arguments)) {
            payload.arguments.forEach(arg => {
              if (typeof arg.field_value === 'string' && assetMap.has(arg.field_value)) {
                const assetInfo = {
                  id: arg.field_value,
                  url: assetMap.get(arg.field_value)!,
                };

                // 根据字段名分配资产
                if (arg.field_name === 'dest_video' || arg.field_name === 'face_image') {
                  result.sourceAsset = assetInfo;
                } else if (arg.field_name === 'dest_image' || arg.field_name === 'source_image') {
                  result.targetAsset = assetInfo;
                } else if (arg.field_name === 'target_image' || arg.field_name === 'mask_image') {
                  result.maskAsset = assetInfo;
                }
              }
            });
          }

          // 兼容旧的直接字段方式
          if (payload.source_asset_id && assetMap.has(payload.source_asset_id)) {
            result.sourceAsset = {
              id: payload.source_asset_id,
              url: assetMap.get(payload.source_asset_id)!,
            };
          }

          if (payload.target_asset_id && assetMap.has(payload.target_asset_id)) {
            result.targetAsset = {
              id: payload.target_asset_id,
              url: assetMap.get(payload.target_asset_id)!,
            };
          }

          if (payload.mask_asset_id && assetMap.has(payload.mask_asset_id)) {
            result.maskAsset = {
              id: payload.mask_asset_id,
              url: assetMap.get(payload.mask_asset_id)!,
            };
          }
        }
      }

      return result;
    } catch (error: any) {
      console.error('应用小工具参数时出错:', error);
      return rejectWithValue(error.message);
    }
  }
);

const toolsGenerationSlice = createSlice({
  name: 'toolsGeneration',
  initialState,
  reducers: {
    // 基础设置
    setSelectedModels: (state, action: PayloadAction<string[]>) => {
      state.selectedModels = action.payload;
    },
    setGenerationCount: (state, action: PayloadAction<number>) => {
      state.generationCount = action.payload;
    },

    // 工具类型
    setActiveToolTab: (state, action: PayloadAction<'换脸' | '换装'>) => {
      state.activeToolTab = action.payload;
      // 切换工具类型时清空相关资产
      if (action.payload === '换脸') {
        state.targetAsset = null;
        state.maskAsset = null;
        state.uploadedFileType = null;
      } else if (action.payload === '换装') {
        state.sourceAsset = null;
        state.uploadedFileType = null;
      }
    },

    // 文件上传相关
    setUploadedFileType: (state, action: PayloadAction<'image' | 'video' | null>) => {
      state.uploadedFileType = action.payload;
    },
    setSourceAsset: (state, action: PayloadAction<{ id: string; url: string } | null>) => {
      state.sourceAsset = action.payload;
    },
    setTargetAsset: (state, action: PayloadAction<{ id: string; url: string } | null>) => {
      state.targetAsset = action.payload;
    },
    setMaskAsset: (state, action: PayloadAction<{ id: string; url: string } | null>) => {
      state.maskAsset = action.payload;
    },

    // 模型数据
    setModels: (state, action: PayloadAction<ModelInfo[]>) => {
      state.models = action.payload;
    },

    // 加载状态
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    // 重置状态
    resetToInitialState: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(applyToolsPayloadWithAssets.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(applyToolsPayloadWithAssets.fulfilled, (state, action) => {
        state.isLoading = false;
        const { payload, sourceAsset, targetAsset, maskAsset } = action.payload;

        // 防御性检查：确保payload存在
        if (!payload) {
          console.error('applyToolsPayloadWithAssets: payload为空');
          return;
        }

        try {
          // 应用基础参数
          if (payload.model_id_list && Array.isArray(payload.model_id_list)) {
            state.selectedModels = payload.model_id_list;
          }

          if (payload.number && payload.number > 0) {
            state.generationCount = payload.number;
          }

          // 应用工具类型 - 根据 arguments 中的字段推断工具类型
          if (payload.arguments && Array.isArray(payload.arguments)) {
            const hasVideoField = payload.arguments.some(
              arg => arg.field_name === 'dest_video' || arg.field_name === 'face_video'
            );
            const hasImageField = payload.arguments.some(
              arg =>
                arg.field_name === 'dest_image' ||
                arg.field_name === 'face_image' ||
                arg.field_name === 'source_image' ||
                arg.field_name === 'target_image'
            );

            // 根据字段推断工具类型
            if (hasVideoField) {
              state.activeToolTab = '换脸'; // 视频换脸
              state.uploadedFileType = 'video';
            } else if (hasImageField) {
              state.activeToolTab = '换脸'; // 图片换脸
              state.uploadedFileType = 'image';
            }
          } else if (payload.tool_type) {
            // 兼容旧的工具类型字段
            state.activeToolTab = payload.tool_type === 'face_swap' ? '换脸' : '换装';
          }

          // 应用资产信息
          state.sourceAsset = sourceAsset;
          state.targetAsset = targetAsset;
          state.maskAsset = maskAsset;

          // 根据资产类型设置上传文件类型（如果没有通过 arguments 推断）
          if (!state.uploadedFileType && (sourceAsset || targetAsset)) {
            // 这里可以根据实际需求判断是图片还是视频
            // 暂时默认为图片
            state.uploadedFileType = 'image';
          }
        } catch (error) {
          console.error('应用小工具payload时发生错误:', error);
        }
      })
      .addCase(applyToolsPayloadWithAssets.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setSelectedModels,
  setGenerationCount,
  setActiveToolTab,
  setUploadedFileType,
  setSourceAsset,
  setTargetAsset,
  setMaskAsset,
  setModels,
  setLoading,
  setError,
  resetToInitialState,
} = toolsGenerationSlice.actions;

export default toolsGenerationSlice.reducer;
